import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Input } from "reactstrap";
import moment from "moment";
import Pagination from "@mui/material/Pagination";
import StudioFilterBar, { readFilterSelect } from "../../common/Components/FilterBar/StudioFilterBar";
import GeoResponsiveList from "../../common/Components/GeoList/GeoResponsiveList";
import StudioRowActions, { StudioRowAction } from "../../common/Components/GeoList/StudioRowActions";
import notifications from "../../common/Components/Notifications/Notification";
import { canUseGiRegistry } from "../../helpers/geoEnabled";
import { deleteCert, listCerts } from "../../services/geoCertApi";
import { geoRecordId } from "../gi-registry/geoPath";
import "../gi-registry/gi-registry.scss";
import GeoPhotoLightbox from "../looms/GeoPhotoLightbox";

const LIMIT = 50;

const readAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null") || {};
  } catch {
    return {};
  }
};

const CertificationsList = () => {
  const history = useHistory();
  const authUser: any = readAuthUser();
  const role = authUser?.users?.role;
  const isSuperAdmin = role === "SUPER_ADMIN";
  const canWrite = canUseGiRegistry(role) && !isSuperAdmin;
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [kind, setKind] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [totCnt, setTotCnt] = useState(0);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [lightboxUrl, setLightboxUrl] = useState("");
  const [lightboxLabel, setLightboxLabel] = useState("");

  const loadList = useCallback(
    async (pageOffset = offset, kindFilter = kind) => {
      setLoading(true);
      setError("");
      try {
        const res: any = await listCerts({
          offset: pageOffset,
          limit: LIMIT,
          kind: kindFilter || undefined,
        });
        if (!res?.success) {
          setRows([]);
          setTotCnt(0);
          setError(res?.message || "Unable to load certifications");
          notifications({
            title: "Error!",
            message: res?.message || "Unable to load certifications",
            type: "danger",
          });
          return;
        }
        setRows(Array.isArray(res?.data?.docs) ? res.data.docs : []);
        setTotCnt(Number(res?.data?.length) || 0);
      } catch {
        setRows([]);
        setTotCnt(0);
        setError("Unable to load certifications");
        notifications({
          title: "Error!",
          message: "Unable to load certifications",
          type: "danger",
        });
      } finally {
        setLoading(false);
      }
    },
    [offset, kind]
  );

  useEffect(() => {
    if (!canUseGiRegistry(role)) {
      history.replace("/dashboard");
      return;
    }
    loadList(offset, kind);
  }, [offset, kind, role, history, loadList]);

  const handleReset = () => {
    setKind("");
    setOffset(0);
    loadList(0, "");
  };

  const handlePageChange = (_: unknown, page: number) => {
    setOffset(page - 1);
  };

  const handleDelete = async (row: any) => {
    const id = geoRecordId(row);
    if (!id || !window.confirm(`Remove ${row?.kindLabel || "this certification"} from the studio?`)) {
      return;
    }
    setBusyId(id);
    try {
      const res: any = await deleteCert(id);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not delete this certification",
          type: "danger",
        });
        return;
      }
      notifications({
        title: "Success!",
        message: "Certification removed",
        type: "success",
      });
      loadList(offset, kind);
    } catch {
      notifications({
        title: "Error!",
        message: "Could not delete this certification",
        type: "danger",
      });
    } finally {
      setBusyId("");
    }
  };

  const totPages = Math.ceil(totCnt / LIMIT) || 0;
  const shortcutsReliable = canWrite && !kind && totCnt <= LIMIT;
  const ownedKeys = new Set(rows.map((row) => String(row?.kindKey || "")));
  const hasSilk = ownedKeys.has("SILK_MARK");
  const hasHandloom = ownedKeys.has("HANDLOOM_MARK");
  const emptyText =
    error && !loading && rows.length === 0
      ? error
      : loading && rows.length === 0
      ? "Loading certifications…"
      : "No certifications recorded yet. Add Silk Mark, Handloom Mark, or another government certification such as India Handloom Brand.";

  const certThumb = (ele: any) =>
    ele?.certificateUrl ? (
      <div className="geo-table-thumbs">
        <button
          type="button"
          onClick={() => {
            setLightboxUrl(ele.certificateUrl);
            setLightboxLabel(ele.kindLabel || "Certificate");
          }}
          aria-label={`View ${ele.kindLabel || "certificate"} picture`}
        >
          <img src={ele.certificateUrl} alt={ele.kindLabel || "Certificate"} />
        </button>
      </div>
    ) : (
      "—"
    );

  const certActions = (ele: any) => {
    const id = geoRecordId(ele);
    const actions: StudioRowAction[] = [
      {
        key: "view",
        label: "View",
        icon: "view",
        onClick: () => history.push(`/certifications/${id}`),
      },
    ];
    if (canWrite) {
      actions.push(
        {
          key: "edit",
          label: "Edit",
          icon: "edit",
          tone: "primary",
          onClick: () => history.push(`/certifications/${id}/edit`),
        },
        {
          key: "delete",
          label: "Delete",
          icon: "delete",
          tone: "danger",
          disabled: busyId === id,
          onClick: () => handleDelete(ele),
        }
      );
    }
    return <StudioRowActions actions={actions} />;
  };

  return (
    <div className="gi-registry-page">
      <div className="gi-toolbar-row">
        <h2>Certification received</h2>
        <StudioFilterBar
          fields={[
            {
              id: "kind",
              label: "Type",
              kind: "select",
              placeholder: "All types",
              value: kind,
              options: [
                { label: "Silk Mark", value: "SILK_MARK" },
                { label: "Handloom Mark", value: "HANDLOOM_MARK" },
                { label: "Other", value: "CUSTOM" },
              ],
              onChange: (value: any) => {
                setKind(readFilterSelect(value));
                setOffset(0);
              },
            },
          ]}
          defaultFieldId="kind"
          onReset={handleReset}
          addTo={canWrite ? "/certifications/new" : undefined}
          addAriaLabel="Add certification"
          hideAdd={!canWrite}
          moreContent={
            canWrite && (shortcutsReliable && (!hasSilk || !hasHandloom)) ? (
              <div className="qf-field">
                <span className="qf-field-label">Shortcuts</span>
                {!hasSilk ? (
                  <Button
                    className="btn-hastkala-outline"
                    onClick={() => history.push("/certifications/new?kind=SILK_MARK")}
                  >
                    Add Silk Mark
                  </Button>
                ) : null}
                {!hasHandloom ? (
                  <Button
                    className="btn-hastkala-outline"
                    onClick={() => history.push("/certifications/new?kind=HANDLOOM_MARK")}
                  >
                    Add Handloom Mark
                  </Button>
                ) : null}
              </div>
            ) : undefined
          }
          wideToolbar={
            <div className="gi-registry-filters gi-registry-filters--loom">
              <Input
                type="select"
                className="geo-filter-status"
                value={kind}
                onChange={(e) => {
                  setKind(e.target.value);
                  setOffset(0);
                }}
              >
                <option value="">All types</option>
                <option value="SILK_MARK">Silk Mark</option>
                <option value="HANDLOOM_MARK">Handloom Mark</option>
                <option value="CUSTOM">Other</option>
              </Input>
              <Button className="btn-hastkala-outline" onClick={handleReset}>
                Reset
              </Button>
              {canWrite ? (
                <>
                  {shortcutsReliable && !hasSilk ? (
                    <Button
                      className="btn-hastkala-outline geo-toolbar-cta"
                      onClick={() => history.push("/certifications/new?kind=SILK_MARK")}
                    >
                      Add Silk Mark
                    </Button>
                  ) : null}
                  {shortcutsReliable && !hasHandloom ? (
                    <Button
                      className="btn-hastkala-outline geo-toolbar-cta"
                      onClick={() => history.push("/certifications/new?kind=HANDLOOM_MARK")}
                    >
                      Add Handloom Mark
                    </Button>
                  ) : null}
                  <Button
                    className="ResetBtn geo-toolbar-cta"
                    onClick={() => history.push("/certifications/new")}
                  >
                    Add certification
                  </Button>
                </>
              ) : null}
            </div>
          }
        />
      </div>
      <GeoResponsiveList
        rows={rows}
        rowKey={(ele) => geoRecordId(ele) || ele.kindKey}
        empty={emptyText}
        actions={(ele) => certActions(ele)}
        columns={[
          {
            key: "cert",
            header: "Certificate",
            className: "geo-col-photos",
            cell: (ele) => certThumb(ele),
          },
          {
            key: "type",
            header: "Type",
            cell: (ele) => ele?.kindLabel || "—",
          },
          {
            key: "number",
            header: "Number",
            cell: (ele) => (
              <>
                <div>{ele?.authorisationNumber || "—"}</div>
                <div className="gi-au-hint" style={{ marginBottom: 0 }}>
                  {ele?.numberLabel || "Authorisation number"}
                </div>
              </>
            ),
          },
          ...(isSuperAdmin
            ? [
                {
                  key: "studio",
                  header: "Studio",
                  hideSm: true,
                  cell: (ele: any) => ele?.orgName || "—",
                },
              ]
            : []),
          {
            key: "added",
            header: "Added",
            hideSm: true,
            cell: (ele) => (ele?.createdAt ? moment(new Date(ele.createdAt)).format("YYYY-MM-DD") : "—"),
          },
        ]}
      />
      {totPages > 1 ? (
        <div className="geo-pagination">
          <Pagination count={totPages} page={offset + 1} onChange={handlePageChange} />
        </div>
      ) : null}
      <GeoPhotoLightbox
        items={lightboxUrl ? [{ url: lightboxUrl, label: lightboxLabel || "Certificate" }] : []}
        index={lightboxUrl ? 0 : -1}
        onClose={() => setLightboxUrl("")}
        onIndex={() => undefined}
      />
    </div>
  );
};

export default CertificationsList;
